import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import HbeLogo from "./HbeLogo";

const links = [
  { to: "/yachts", label: "Yachts", testid: "nav-yachts" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/faq", label: "FAQ", testid: "nav-faq" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

// Distance from top of `/` before the nav flips from transparent to glass.
const DARK_MAX_SCROLL = 80;

/**
 * Public site header — Apple/Vision-Pro-style translucent glass.
 *
 *   `dark`  → only on `/` while scrollY < 80. FULLY transparent, no border,
 *             no shadow, white logo, white nav links, outlined-white CTA.
 *   `light` → every other state. `bg-white/60 backdrop-blur-xl saturate-150`,
 *             navy logo, dark ink, filled-navy CTA.
 *
 * The header is `position: fixed` so the hero image can bleed to the top of
 * the viewport underneath a transparent nav. `Layout.jsx` reserves the
 * corresponding height with `pt-[52px] md:pt-[56px]` on <main>, and the
 * home hero cancels that padding with a negative margin so it sits under
 * the fixed nav.
 */
export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!isHome) {
      setScrollY(DARK_MAX_SCROLL + 1);
      return;
    }
    const onScroll = () => setScrollY(window.scrollY);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  // Force the light state when the mobile drawer is open so the drawer
  // reads naturally under a solid header.
  const mode =
    !open && isHome && scrollY < DARK_MAX_SCROLL ? "dark" : "light";
  const isDark = mode === "dark";

  const headerCls = isDark
    ? "bg-transparent"
    : "bg-white/60 backdrop-blur-xl backdrop-saturate-150 border-b border-black/[0.06] shadow-[0_1px_20px_-8px_rgba(6,40,67,0.10)]";

  const linkBase =
    "relative text-sm font-medium py-1 transition-colors after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-current after:origin-center after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100";
  const linkInk = isDark
    ? "text-white/85 hover:text-white"
    : "text-hbe-ink/70 hover:text-hbe-primary";
  const linkInkActive = isDark ? "text-white" : "text-hbe-primary";
  const toggleCls = isDark ? "text-white" : "text-hbe-primary";
  const ctaCls = isDark
    ? "hbe-btn-ghost hbe-btn-ghost-on-dark !py-2 !px-5 !text-[0.85rem] !min-h-[38px]"
    : "hbe-btn-primary !py-2 !px-5 !text-[0.85rem] !min-h-[38px]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300 ease-out ${headerCls}`}
      data-testid="site-nav"
      data-nav-mode={mode}
    >
      <div className="hbe-container flex items-center justify-between min-h-[52px] md:min-h-[56px] py-1.5 md:py-2">
        <Link
          to="/"
          className="inline-flex items-center"
          data-testid="brand-wordmark"
          aria-label="HBE Entertainment home"
        >
          <HbeLogo
            variant="lockup"
            color={isDark ? "white" : "navy"}
            size={36}
            className="md:hidden"
          />
          <HbeLogo
            variant="lockup"
            color={isDark ? "white" : "navy"}
            size={40}
            className="hidden md:inline-flex"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkInkActive : linkInk}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/yachts" className={ctaCls} data-testid="nav-book-cta">
            Book a Yacht
          </Link>
        </nav>

        <button
          type="button"
          className={`md:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 ${toggleCls}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-black/[0.06] bg-white/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="hbe-container py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                className="py-3 text-base text-hbe-ink"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/yachts"
              className="hbe-btn-primary mt-3 w-full"
              data-testid="m-nav-book-cta"
            >
              Book a Yacht
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
